import { CONTACT_EMAIL } from 'app/../app.config.json';
import { InsuredUserDetailsSheet } from 'app/components/insured-users';
import { Column, Row } from 'app/design/primitives/box';
import { SquareLogo } from 'app/design/icons/square-logo';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useNavigationOptions } from 'app/hooks/navigation';
import { getI18nLanguage } from 'app/lib/language';
import { safelyOpenURL } from 'app/lib/navigation';
import { preparePolicyUrl } from 'app/lib/text';
import { env } from 'app/lib/env';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { useUser } from 'app/network/user';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { Button, useTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import type { Profile } from 'app/types/profile';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isWhiteLabel } from 'app/utils/helpers';
import { usePageTitle } from 'app/utils/web-helpers';
import {
  IconBook2,
  IconChevronRight,
  IconCreditCard,
  IconHelpCircle,
  IconMail,
  IconMessages,
  IconShieldCheck,
  IconRosetteDiscountCheckFilled,
  IconSettings,
} from '@tabler/icons-react-native';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';

type SupportActions = {
  onHelp: () => void;
  onCompensation: () => void;
  onPlan: () => void;
  onFaq: () => void;
  onEmail: () => void;
  onContact: () => void;
};

export function SupportProfileContent({
  brandName,
  isSaas,
  onHelp,
  onCompensation,
  onPlan,
  onFaq,
  onEmail,
  onContact,
}: SupportActions & { brandName: string; isSaas: boolean }) {
  const { t } = useTranslation('support-profile');
  const theme = useTheme();
  const links = [
    { label: t('loanGuide'), onPress: onHelp, icon: IconBook2 },
    ...(!isSaas
      ? [{ label: t('details.title', { ns: 'insured' }), onPress: onCompensation, icon: IconShieldCheck }]
      : []),
    {
      label: t(isSaas ? 'manageAccount' : 'managePlan'),
      onPress: onPlan,
      icon: isSaas ? IconSettings : IconCreditCard,
    },
    { label: t('helpCenter'), onPress: onFaq, icon: IconHelpCircle },
  ];
  return (
    <Column gap="spacing.general.x5">
      <Column
        backgroundColor="surface.brand.subtle"
        borderRadius="radius.xl"
        padding="spacing.general.x4"
        gap="spacing.general.x4"
      >
        <Row alignItems="center" gap="spacing.general.x3">
          <SquareLogo height={56} width={56} />
          <Column flex={1} gap="spacing.general.x2">
            <Text variant="headerXSmall" color="text.brand">
              {brandName}
            </Text>
            <Row
              alignSelf="flex-start"
              alignItems="center"
              gap="spacing.general.x1"
              backgroundColor="surface.default"
              borderRadius="radius.full"
              paddingHorizontal="spacing.general.x2"
              paddingVertical="spacing.general.x1"
            >
              <IconRosetteDiscountCheckFilled size={22} color={theme.colors['icon.emphasized']} />
              <Text variant="bodySmall" color="text.brand" fontWeight="600">
                {t('officialTeam')}
              </Text>
            </Row>
          </Column>
        </Row>
        <Column gap="spacing.general.x2">
          <Text variant="headerXSmall" color="text.brand">
            {t('welcomeTitle')}
          </Text>
          <Text variant="bodyBase" color="text.secondary">
            {t('welcomeDescription', { team: brandName })}
          </Text>
        </Column>
        <Button
          variant="primary"
          size="large"
          fullWidth
          label={t('contactSupport')}
          leadingIcon={({ color, size }) => <IconMessages size={size} color={color} strokeWidth={ICON_STROKE_WIDTH} />}
          onPress={onContact}
        />
      </Column>

      <Column gap="spacing.general.x3">
        <Text variant="bodySmall" color="text.secondary" fontWeight="600">
          {t('resourcesTitle')}
        </Text>
        <Column borderColor="border.secondary" borderWidth={1} borderRadius="radius.lg" overflow="hidden">
          {links.map(({ label, onPress, icon: Icon }, index) => (
            <Pressable
              key={label}
              accessibilityRole="button"
              onPress={onPress}
              style={({ pressed }) => ({
                minHeight: 64,
                paddingHorizontal: theme.spacing['spacing.general.x4'],
                paddingVertical: theme.spacing['spacing.general.x3'],
                borderTopWidth: index ? 1 : 0,
                borderTopColor: theme.colors['border.divider'],
                backgroundColor: theme.colors[pressed ? 'surface.brand.subtle' : 'surface.default'],
              })}
            >
              <Row alignItems="center" gap="spacing.general.x3">
                <Column
                  width={36}
                  height={36}
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="radius.md"
                  backgroundColor="surface.brand.subtle"
                >
                  <Icon size={20} color={theme.colors['icon.emphasized']} strokeWidth={ICON_STROKE_WIDTH} />
                </Column>
                <Text variant="bodyBase" flex={1}>
                  {label}
                </Text>
                <IconChevronRight size={20} color={theme.colors['icon.secondary']} strokeWidth={ICON_STROKE_WIDTH} />
              </Row>
            </Pressable>
          ))}
        </Column>
      </Column>

      <Pressable
        accessibilityRole="link"
        onPress={onEmail}
        style={({ pressed }) => ({
          minHeight: 64,
          padding: theme.spacing['spacing.general.x4'],
          borderRadius: theme.borderRadii['radius.lg'],
          backgroundColor: theme.colors[pressed ? 'surface.brand.subtle' : 'surface.primary-neutral'],
        })}
      >
        <Row alignItems="center" gap="spacing.general.x3">
          <IconMail size={22} color={theme.colors['icon.emphasized']} strokeWidth={ICON_STROKE_WIDTH} />
          <Column flex={1} gap="spacing.general.x1">
            <Text variant="bodyBase" fontWeight="600">
              {t('email')}
            </Text>
            <Text variant="bodySmall" color="text.secondary">
              {CONTACT_EMAIL}
            </Text>
          </Column>
          <IconChevronRight size={20} color={theme.colors['icon.secondary']} strokeWidth={ICON_STROKE_WIDTH} />
        </Row>
      </Pressable>
    </Column>
  );
}

export function SupportProfileScreen({
  navigation,
}: React.ComponentProps<NavigationScreen<'neighbour-profile'>> & { profile: Profile }) {
  const { t } = useTranslation('support-profile');
  const theme = useTheme();
  const padding = usePagePadding();
  const { data: user } = useUser();
  const language = getI18nLanguage();
  const isSaas = isWhiteLabel();
  const [showResponsibility, setShowResponsibility] = useState(false);
  const setPageTitle = usePageTitle();
  useEffect(() => setPageTitle(t('title')), [setPageTitle, t]);
  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {t('title')}
      </Text>
    ),
    headerRight: () => null,
  });
  const openFaq = () => void safelyOpenURL(preparePolicyUrl(t('faqLink', { ns: 'contact-us' }), language, user));
  const openCompensation = () => {
    if (!isSaas) setShowResponsibility(true);
  };
  const openPlan = () =>
    navigation.navigate('main', {
      screen: 'profile',
      params: isSaas ? { screen: 'my-information' } : { screen: 'my-plan' },
    });
  return (
    <Page
      showsVerticalScrollIndicator={false}
      paddingStyle={{ ...padding, paddingTop: theme.spacing['spacing.general.x3'] }}
    >
      <SupportProfileContent
        brandName={env.APP_NAME}
        isSaas={isSaas}
        onHelp={() => navigation.navigate('help')}
        onCompensation={openCompensation}
        onPlan={openPlan}
        onFaq={openFaq}
        onEmail={() => void safelyOpenURL(`mailto:${CONTACT_EMAIL}`)}
        onContact={() => navigateToChat(navigation, SUPPORT_CHANNEL_URL)}
      />
      {!isSaas && showResponsibility ? (
        <InsuredUserDetailsSheet details={{ insured: true }} onClose={() => setShowResponsibility(false)} />
      ) : null}
    </Page>
  );
}
